/**
 * RequestItem component for displaying and managing individual requests.
 */

// #region Imports

import React, { useState, useRef, useEffect } from 'react';
import { DateFormat } from 'date-display';
import CopyTextButton from './CopyTextButton';
import RequestItemContainer from './RequestItemContainer';
import { requestTypes, requestTypeLabels } from './const';

import {
  type ClientRequest,
  type ClientRequestType,
  createdStatus,
  completedStatus,
  NewClientRequest,
  generalRequestType,
  ErrorResponse,
} from './ClientRequestService';
import { generateRequestLink } from './generateRequestLink';
import { useClientRequest } from './useClientRequest';
import { TextareaAutosize } from '@benchlabs/react-design-system';
import { type NotifyFunction } from './NotificationsService';
// #endregion

const genericErrorMessage = 'Problem found! Please try again!';

/**
 * Props for the RequestItem component.
 */
interface RequestItemProps {
  /**
   * The request object containing request data. This can be undefined
   * if the request is new and has not been saved yet. If undefined,
   * then `isNew` must be true.
   */
  request?: ClientRequest;

  /**
   * Indicates if this is a new request being created
   * If true then the `request` property must be undefined.
   */
  isNew?: boolean;

  /**
   * The client ID for which the request belongs.
   */
  clientId: string;

  /**
   * Index used for generating test IDs
   */
  testIdIndex?: string | number;

  /**
   * Callback function called when canceling new request creation.
   */
  onCancelNewRequest?: () => void;

  /**
   * Callback function called when saving a new request.
   */
  onSaveNewRequest?: (newRequest: NewClientRequest) => Promise<void>;

  /**
   * Callback function called when request is saved or completed.
   */
  onSaveOrComplete: () => void;

  /**
   * Function to display notifications.
   */
  notify?: NotifyFunction;
}

/**
 * RequestItem component for displaying and managing individual requests.
 * @param props - Props for the RequestItem component.
 * @returns JSX.Element representing the RequestItem component.
 */
const RequestItem: React.FC<RequestItemProps> = ({
  request,
  isNew = false,
  testIdIndex,
  clientId,
  onCancelNewRequest,
  onSaveNewRequest,
  onSaveOrComplete,
  notify,
}) => {
  // track request properties separately to allow editing
  const [name, setName] = useState(request?.name || '');
  const [status, setStatus] = useState(request?.status || '');
  const [url, setUrl] = useState(request ? generateRequestLink(request) : '');
  const [requestType, setRequestType] = useState<ClientRequestType>(request?.requestType || 'NONE');
  const [requestError, setRequestError] = useState<string | null>(null);

  // track if we are in edit mode -- can be changed by different interactions
  const [showEditRequest, setShowEditRequest] = useState(isNew);

  const { updateStatus, updateRequest } = useClientRequest(clientId, request || null);

  // #region Helper Functions
  const getTestIdStatus = () => (isNew ? 'NEW' : status);
  const isRequestComplete = () => status === completedStatus;

  /**
   * Controls the behavior of clicking the checkbox for a request.
   * @returns Nothing
   */
  const toggleCompleteRequest = () => {
    return updateStatus(isRequestComplete() ? createdStatus : completedStatus).then(() =>
      onSaveOrComplete?.(),
    );
  };

  /**
   * Determines if the form is valid.
   * @returns True if the form is valid, false if not.
   */
  const isFormValid = (): boolean => {
    if (!name.trim()) {
      setRequestError('Request name is required.');
      return false;
    }

    if (!requestType || requestType === 'NONE') {
      setRequestError('Request type is missing!');
      return false;
    }

    setRequestError(null);
    return true;
  };

  /**
   * Closes edit mode and calls appropriate callbacks.
   * If this is a new request, it calls onCancelNewRequest.
   * If this is an existing request and changes were made,
   * it calls onSaveOrComplete.
   * @returns Nothing
   */
  const closeEditRequest = () => {
    if (isNew) {
      onCancelNewRequest?.();
    } else {
      const nameChanged = request?.name !== name;
      const typeChanged = request?.requestType !== requestType;
      const urlChanged = request?.metadata?.url !== url;

      // if anything has changed then call onSaveOrComplete
      if (nameChanged || typeChanged || urlChanged) {
        onSaveOrComplete?.();
      }

      setShowEditRequest(false);
    }
  };

  const saveOrUpdate = async () => {
    if (!isFormValid()) {
      return;
    }

    // determine if we have any metadata to send
    const metadata = requestType === generalRequestType && url ? { url } : undefined;

    if (isNew) {
      const newRequest: NewClientRequest = {
        name,
        requestType: requestType,
        metadata,
      };

      // API request done by parent element
      onSaveNewRequest?.(newRequest).catch((error: ErrorResponse) => {
        const message = error.response?.data?.messages[0]?.message ?? genericErrorMessage;
        setRequestError(message);
      });
    } else {
      // create a clone of existing request with updated values
      const updatedRequest: ClientRequest = {
        ...(request as ClientRequest),
        name,
        requestType,
        metadata,
      };

      updateRequest(updatedRequest)
        .then(() => {
          setShowEditRequest(false);
          setUrl(generateRequestLink(updatedRequest));
        })
        .catch((error: ErrorResponse) => {
          const message = error.response?.data?.messages[0]?.message ?? genericErrorMessage;
          setRequestError(message);
        });
    }
  };

  // #endregion Helper Functions

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus the textarea when showEditRequest becomes true
  useEffect(() => {
    if (showEditRequest && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showEditRequest]);

  return (
    <RequestItemContainer>
      <div
        className={`request-item_container ${isRequestComplete() ? 'is-complete' : ''}`}
        data-testid={`request-${testIdIndex}-${getTestIdStatus()}`}
      >
        {/* Complete/Cancel Button - acts like a checkbox */}
        {!isNew && (
          <button
            onClick={() => toggleCompleteRequest()}
            data-testid={`button-request-${testIdIndex}-cancel-new`}
            className="request-item_button"
          >
            <span
              className={`bl-icon request-item_button-icon ${
                isRequestComplete()
                  ? 'bl-icon-checked-alt'
                  : 'bl-icon-unchecked request-item_state-icon'
              }`}
            ></span>
          </button>
        )}

        {/* New Request Indicator (Plus Icon) */}
        {isNew && (
          <div className="request-item_button">
            <span className="bl-icon bl-icon-plus requests_object-add-icon"></span>
          </div>
        )}

        {/* Request Title - Always displayed in a textbox */}
        <div className="request-item_content-container">
          <div className="request-item_header">
            <div className="request-item_title-container">
              <label
                data-testid={`request-${testIdIndex}-title-type-${getTestIdStatus()}`}
                className="request-item_label"
              >
                <span>
                  {requestType && requestType !== 'NONE' && requestTypeLabels[requestType]}
                </span>
              </label>
              <TextareaAutosize
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={!showEditRequest}
                data-testid={`request-${testIdIndex}-title-${getTestIdStatus()}`}
                placeholder="Request title"
                className="request-item_title"
              />
            </div>

            {/* Edit and Copy Buttons - only when not editing and not complete */}
            {!showEditRequest && !isRequestComplete() && (
              <div className="request-item_actions">
                {/* Edit Button */}
                <button
                  onClick={() => setShowEditRequest(true)}
                  data-testid={`button-request-${testIdIndex}-edit-${getTestIdStatus()}`}
                  className="request-item_button request-item_edit"
                >
                  <span className="bl-icon bl-icon-edit request-item_button-icon"></span>
                </button>

                {/* Copy Link Button */}
                <CopyTextButton
                  textToCopy={url}
                  testId={`button-request-${testIdIndex}-copy-${getTestIdStatus()}`}
                  onCopySuccess={() => {
                    notify?.('Link copied to clipboard!', { type: 'success', length: 3 });
                  }}
                  onCopyError={error => {
                    notify?.('Failed to copy link.', { type: 'error', length: 3 });
                  }}
                  className="request-item_button request-item_copy"
                />
              </div>
            )}

            {/* Request Completed Date - only when complete */}
            {isRequestComplete() && request && (
              <span
                data-testid={`request-${testIdIndex}-date-${getTestIdStatus()}`}
                className="request-item_date"
              >
                <DateFormat date={request.dateUpdated} format="YYYY-MM-DD" />
              </span>
            )}
          </div>

          {/* Request Details - only when editing */}
          {showEditRequest && (
            <div className="request-item_details bl-form">
              <div className="request-item_field">
                <label htmlFor={`request-${testIdIndex}-type-select`}>Request Type</label>
                <div className="request-item_select-container">
                  <select
                    id={`request-${testIdIndex}-type-select`}
                    value={requestType}
                    onChange={e => {
                      setRequestType(e.target.value as ClientRequestType);
                    }}
                    data-testid={`request-${testIdIndex}-type-${getTestIdStatus()}`}
                    className="request-item_select"
                  >
                    {requestTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <div className="request-item_select-icon">
                    <span className="bl-icon bl-icon-collapse-down request-item_button-icon"></span>
                  </div>
                </div>
              </div>

              {requestType === 'GENERAL' && (
                <div className="request-item_field">
                  <label htmlFor={`request-${testIdIndex}-url-input`}>Optional Request Link</label>
                  <input
                    id={`request-${testIdIndex}-url-input`}
                    value={url}
                    onChange={e => {
                      setUrl(e.target.value);
                    }}
                    data-testid={`request-${testIdIndex}-url-${getTestIdStatus()}`}
                    placeholder="Add optional url..."
                    type="text"
                  />
                </div>
              )}

              {/* Cancel and Save Buttons */}
              <div className="request-item_field">
                <button
                  onClick={closeEditRequest}
                  data-testid={`button-request-${testIdIndex}-cancel-${getTestIdStatus()}`}
                  className="bl-button"
                >
                  Cancel
                </button>
                &nbsp;
                <button
                  onClick={saveOrUpdate}
                  data-testid={`button-request-${testIdIndex}-save-${getTestIdStatus()}`}
                  className="bl-button primary"
                >
                  Save
                </button>
              </div>

              {/* Error Message */}
              {requestError && <div className="error-message">Error: {requestError}</div>}
            </div>
          )}
        </div>
      </div>
    </RequestItemContainer>
  );
};

export default RequestItem;
